import { fromUnixTime } from 'date-fns';
import type { MintSchedule } from '@soundxyz/sdk/types';
import { getFromList } from '@soundxyz/utils';
import {
  getMinSupply,
  getMintExpiry,
  getScheduleId,
  toMintTypeFromSchedule,
} from '@components/Song/MintComponents/helpers';
import { dropdownValueKey } from '@components/Song/MintComponents/shared-helpers';

export function mapSelectedSchedule(schedule: MintSchedule) {
  return {
    schedule,
    key: dropdownValueKey({
      type: toMintTypeFromSchedule(schedule),
      id: getScheduleId(schedule),
    }),
  };
}

export const getDefaultSelectedSchedule = ({
  schedules,
}: {
  schedules: {
    schedule: MintSchedule;
    hasSaleAccess: boolean;
  }[];
}) => {
  const now = new Date();
  const activePresalePanel = getFromList(
    schedules,
    ({ schedule, hasSaleAccess }) =>
      schedule.mintType === 'MerkleDrop' &&
      hasSaleAccess &&
      fromUnixTime(schedule.endTime) > now &&
      mapSelectedSchedule(schedule),
  );

  const activePublicSchedulePanel = getFromList(
    schedules,
    ({ schedule, hasSaleAccess }) =>
      schedule.mintType !== 'MerkleDrop' &&
      hasSaleAccess && {
        schedule,
        key: dropdownValueKey({
          type: toMintTypeFromSchedule(schedule),
          id: getScheduleId(schedule),
        }),
      },
  );

  return activePresalePanel || activePublicSchedulePanel || null;
};

export const getPanelSubText = ({
  hasSaleAccess,
  soldOut,
  hasScheduleEligibility,
  saleEnded,
}: {
  hasSaleAccess: boolean;
  soldOut: boolean;
  hasScheduleEligibility: boolean;
  saleEnded: boolean;
}) => {
  if (soldOut) {
    return 'Sold Out';
  }

  if (saleEnded) {
    return 'Ended';
  }

  if (!hasSaleAccess && hasScheduleEligibility) {
    return "You've hit the cap";
  }

  return null;
};

export const hasScheduleRangeExpired = ({
  schedule,
  totalMinted,
}: {
  schedule: MintSchedule;
  totalMinted: number;
}) => {
  const now = new Date();
  const mintExpiryMs = getMintExpiry(schedule);
  const minSupply = getMinSupply(schedule);

  const timePassed = now.getTime() > fromUnixTime(mintExpiryMs).getTime();
  const minNotHit = totalMinted < minSupply;

  return timePassed && minNotHit;
};

/**
 * this helper takes into account when an open edition expires and the min was not hit
 * in this case it turns into a fixed edition thus the max supply is the lower bound
 */
export const getMaxSupplyWithRangeExpiredCheck = ({
  schedule,
  totalMinted,
}: {
  schedule: MintSchedule;
  totalMinted: number;
}): number => {
  const type = schedule.mintType;

  switch (type) {
    case 'MerkleDrop':
      return schedule.maxMintable;
    case 'RangeEdition':
      const scheduleExpired = hasScheduleRangeExpired({
        schedule,
        totalMinted,
      });
      if (scheduleExpired) return schedule.maxMintableLower;
      return schedule.maxMintableUpper;
    default:
      throw Error(`Could not get max supply for schedule ${type}`);
  }
};

export const checkShowUrgency = ({
  hasSaleAccess,
  hasScheduleEligibility,
  saleStarted,
  activeSale,
}: {
  hasSaleAccess: boolean;
  hasScheduleEligibility: boolean;
  saleStarted: boolean;
  activeSale: boolean;
}) => {
  const capHit = !hasSaleAccess && hasScheduleEligibility;
  // sale not started
  if (!saleStarted) return true;

  // sale ongoing and user has maxed out
  if (activeSale && !capHit) return true;

  return false;
};

export function isMintSchedulePresale(schedule: MintSchedule) {
  return schedule.mintType === 'MerkleDrop';
}
